import {
  BarChart2,
  Box,
  Briefcase,
  Cloud,
  Code2,
  Database,
  ExternalLink,
  FileText,
  Globe,
  Lock,
  type LucideIcon,
  Mail,
  MessageSquare,
  Monitor,
  Server,
  Settings,
  Settings2,
  Share2,
  Shield,
  ShoppingCart,
  Star,
  Ticket,
  Users,
  Zap,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ApiApp } from "../lib/api";
import { RuntimeStatusBadge } from "./RuntimeStatusBadge";

const ACCENT_COLORS = ["#d4521a", "#c27d00", "#2a6db5", "#3a9e5f", "#6a5acd", "#555555"] as const;

const ICON_KEYWORDS: [string, LucideIcon][] = [
  ["sap", Database],
  ["salesforce", Users],
  ["mail", Mail],
  ["inbox", Mail],
  ["ki", Zap],
  [" ai", Zap],
  ["automat", Zap],
  ["analytic", BarChart2],
  ["report", BarChart2],
  ["chart", BarChart2],
  ["hr", Briefcase],
  ["personal", Briefcase],
  ["logistik", ShoppingCart],
  ["shop", ShoppingCart],
  ["order", ShoppingCart],
  ["security", Shield],
  ["auth", Lock],
  ["access", Lock],
  ["monitor", Monitor],
  ["cloud", Cloud],
  ["azure", Cloud],
  ["aws", Cloud],
  ["dev", Code2],
  ["code", Code2],
  ["server", Server],
  ["backend", Server],
  ["doc", FileText],
  ["wiki", FileText],
  ["ticket", Ticket],
  ["issue", Ticket],
  ["chat", MessageSquare],
  ["support", MessageSquare],
  ["web", Globe],
  ["portal", Globe],
  ["config", Settings2],
  ["admin", Settings2],
];

function accentColor(slug: string): string {
  const hash = [...slug].reduce((a, c) => a + c.charCodeAt(0), 0);
  return ACCENT_COLORS[hash % ACCENT_COLORS.length] ?? "#d4521a";
}

function resolveIcon(app: ApiApp): LucideIcon {
  // Image-derived brand/product feed the keyword match too — an image that
  // labels itself `de.mindsquare.agenthub.brand=postgresql` resolves to the
  // database icon even when name/slug give no hint.
  const haystack = [
    app.name,
    app.category ?? "",
    app.slug,
    app.metadata?.brand ?? "",
    app.metadata?.product ?? "",
  ]
    .join(" ")
    .toLowerCase();
  for (const [kw, icon] of ICON_KEYWORDS) {
    if (haystack.includes(kw)) return icon;
  }
  return Box;
}

export interface AppTileProps {
  app: ApiApp;
  /** #38: aktueller Favoriten-Zustand (optimistisch vom Launchpad gesteuert). */
  isFavorite?: boolean;
  /** #38: Favoriten-Toggle; wenn gesetzt, wird der Stern angezeigt. */
  onToggleFavorite?: () => void;
  /**
   * #103-Folge: Verwalten-Zahnrad. Gesetzt, wenn der aktuelle Nutzer diese App
   * verwalten darf (Ersteller ODER Admin/Operator) — öffnet die App-Detailseite.
   */
  onManage?: (app: ApiApp) => void;
  /**
   * #6: Teilen-Icon. Gesetzt, wenn der Nutzer die App verwalten darf UND sie
   * `shareable` ist (Showroom) — öffnet die Einstellungen auf dem Teilen-Tab.
   */
  onShare?: () => void;
  /** #6: faerbt das Teilen-Icon, wenn mindestens ein aktiver Gast-Link existiert. */
  hasActiveShareLink?: boolean;
}

export function AppTile({
  app,
  isFavorite = false,
  onToggleFavorite,
  onManage,
  onShare,
  hasActiveShareLink = false,
}: AppTileProps): JSX.Element {
  const { t } = useTranslation();
  const isExternal = app.app_type === "external";
  const href = isExternal ? (app.target_url ?? "#") : (app.public_path ?? "#");
  const linkProps = isExternal
    ? { target: "_blank" as const, rel: "noopener noreferrer" as const }
    : {};

  const color = accentColor(app.slug);
  const Icon = resolveIcon(app);
  const hasEmoji = app.icon != null;

  const rt = app.runtime;
  const inFlight = rt?.observed_state === "pulling" || rt?.observed_state === "starting";
  // #103: „läuft & gesund" ist der Normalfall und damit kein Informationsgewinn —
  // den Status-Badge nur zeigen, wenn der Zustand davon abweicht.
  const showStatus = rt != null && rt.observed_state !== "healthy";

  return (
    <a
      className="app-tile"
      href={href}
      style={{ "--app-accent": color } as React.CSSProperties}
      {...linkProps}
    >
      <div className="app-tile__corner">
        {onShare ? (
          <button
            type="button"
            className={`app-tile__corner-btn app-tile__share${hasActiveShareLink ? " app-tile__share--active" : ""}`}
            aria-label={t("launchpad.share")}
            title={t("launchpad.share")}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onShare();
            }}
          >
            <Share2 size={16} aria-hidden="true" />
          </button>
        ) : null}
        {onManage ? (
          <button
            type="button"
            className="app-tile__corner-btn"
            aria-label={t("launchpad.manage")}
            title={t("launchpad.manage")}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onManage(app);
            }}
          >
            <Settings size={16} aria-hidden="true" />
          </button>
        ) : null}
        {onToggleFavorite ? (
          <button
            type="button"
            className={`app-tile__corner-btn app-tile__fav${isFavorite ? " app-tile__fav--on" : ""}`}
            aria-label={t(isFavorite ? "launchpad.unfavorite" : "launchpad.favorite")}
            aria-pressed={isFavorite}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleFavorite();
            }}
          >
            <Star size={16} fill={isFavorite ? "currentColor" : "none"} aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <div
        className={`app-tile__icon${hasEmoji ? " app-tile__icon--emoji" : ""}`}
        aria-hidden="true"
      >
        {hasEmoji ? app.icon : <Icon size={22} strokeWidth={2} />}
        {rt?.update_available && app.is_owner ? (
          <span className="app-tile__update-dot" title={t("launchpad.updateAvailable")} />
        ) : null}
      </div>
      <div className="app-tile__body">
        {app.category ? <span className="app-tile__category">{app.category}</span> : null}
        <h3 className="app-tile__title">
          {app.name}
          {isExternal ? (
            <ExternalLink
              size={13}
              className="app-tile__external"
              aria-label={t("launchpad.opensExternally")}
            />
          ) : null}
        </h3>
        {app.description ? <p className="app-tile__description">{app.description}</p> : null}
        {app.metadata?.vendor ? (
          <span className="app-tile__vendor">{app.metadata.vendor}</span>
        ) : null}
        {app.is_owner && app.visibility === "private" ? (
          <span className="app-tile__badge-private">{t("launchpad.visibility.privateBadge")}</span>
        ) : null}
        {/* Status-Footer am unteren Kachelrand — nur bei abweichendem Zustand. */}
        {showStatus ? (
          <div className="app-tile__runtime">
            <RuntimeStatusBadge state={rt.observed_state} />
            {inFlight ? <span className="app-tile__progress" aria-hidden="true" /> : null}
          </div>
        ) : null}
      </div>
    </a>
  );
}
